/**
 * Reading values out of parsed JSON that is not trusted to be anything.
 *
 * A saved score comes back as `unknown`, and casting it to a `Score` would move the
 * failure from the moment the file is opened to some later moment inside a renderer,
 * where the message is about a property of undefined rather than about the file. Every
 * field is therefore read and checked, and a file that is missing one, or has the wrong
 * kind of thing in it, is refused by name and path.
 *
 * The readers throw and {@link readDocument} catches, which is the one place in this
 * package where a throw is used as control flow. Threading a result type through forty
 * nested readers would bury the shape of the document under the error handling, and the
 * throw never escapes: everything public here answers with a value or a reason.
 */

import type { Id, IdKind } from "../document/ids";

/** A file that is not a score, with the path of the first thing wrong with it. */
export class ReadFault extends Error {
  constructor(
    readonly at: string,
    readonly wanted: string,
  ) {
    super(`${at === "" ? "the document" : at} should be ${wanted}`);
    this.name = "ReadFault";
  }
}

const fault = (at: string, wanted: string): never => {
  throw new ReadFault(at, wanted);
};

/** A JSON object, refusing arrays and `null`, which `typeof` calls objects too. */
export function record(value: unknown, at: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    fault(at, "an object");
  }
  return value as Record<string, unknown>;
}

/** One field of an object, or `undefined` when it is not there. */
export function field(source: unknown, key: string, at: string): unknown {
  return record(source, at)[key];
}

/** Read `key` of `source` with `read`, reporting the path if it is wrong. */
export function at<T>(
  source: unknown,
  key: string,
  path: string,
  read: (value: unknown, at: string) => T,
): T {
  const where = path === "" ? key : `${path}.${key}`;
  return read(field(source, key, path), where);
}

export function text(value: unknown, at: string): string {
  return typeof value === "string" ? value : (fault(at, "a string") as never);
}

/** A length or any other measured quantity: a finite number, fractions allowed. */
export function measured(value: unknown, at: string): number {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : (fault(at, "a number") as never);
}

export function whole(value: unknown, at: string): number {
  return typeof value === "number" && Number.isInteger(value)
    ? value
    : (fault(at, "a whole number") as never);
}

export function counted(value: unknown, at: string): number {
  const number = whole(value, at);
  return number >= 0 ? number : (fault(at, "not negative") as never);
}

export function flag(value: unknown, at: string): boolean {
  return typeof value === "boolean" ? value : (fault(at, "true or false") as never);
}

/**
 * One of a fixed set of strings.
 *
 * This is what keeps a file from bringing in a clef sign or an articulation the rest of
 * the package has never heard of, which would otherwise travel all the way to the
 * exporter before anything noticed.
 */
export function oneOf<T extends string>(allowed: readonly T[], value: unknown, at: string): T {
  const found = text(value, at);
  return allowed.includes(found as T)
    ? (found as T)
    : (fault(at, `one of ${allowed.join(", ")}`) as never);
}

/** A value that is allowed to be `null`, which in this document means "nothing here". */
export function maybe<T>(
  read: (value: unknown, at: string) => T,
  value: unknown,
  at: string,
): T | null {
  return value === null || value === undefined ? null : read(value, at);
}

/** An array, each element read the same way and reported by its index. */
export function list<T>(read: (value: unknown, at: string) => T, value: unknown, at: string): T[] {
  if (!Array.isArray(value)) fault(at, "a list");
  return (value as unknown[]).map((entry, index) => read(entry, `${at}[${index}]`));
}

/**
 * An id.
 *
 * Only the string is required, not the `kind-random-counter` shape this package mints. A
 * document that came from somewhere else -- an importer, another program, a file written
 * by hand -- carries ids that are perfectly good identity without following our spelling,
 * and refusing them would refuse documents that work.
 */
export function identifier<Kind extends IdKind>(value: unknown, at: string): Id<Kind> {
  const found = text(value, at);
  return found === "" ? (fault(at, "a non-empty id") as never) : (found as Id<Kind>);
}

/** A map keyed by id, each value read the same way. */
export function keyed<T>(
  read: (value: unknown, at: string) => T,
  value: unknown,
  path: string,
): Record<string, T> {
  const source = record(value, path);
  const result: Record<string, T> = {};
  for (const [key, entry] of Object.entries(source)) {
    result[key] = read(entry, `${path}.${key}`);
  }
  return result;
}
